import { useCallback, useEffect, useState } from 'react';

import {
  listMyEvents, requestVenue, updateEvent, VENUE_BOOKING_LABEL,
  type OrganizerEvent, type DirectoryVenue,
} from '../../lib/organizer';
import { ApiError } from '../../lib/apiClient';
import { formatDate } from '../../lib/format';
import { VenuePicker } from './VenuePicker';
import './OrganizerPages.css';

/**
 * Standalone venue directory for organizers.
 *
 * A booking request is always FOR an event — POST /api/venue/:id/request takes an
 * eventId — so this screen asks which event first rather than letting someone
 * browse their way into a dead end. With no events yet, it says so instead of
 * showing a directory nothing can be done with.
 *
 * Requesting also offers to copy the venue's name and city onto the event's
 * free-text display fields. Those are a genuinely separate concept from
 * venue_profile_id and the engine does not link them, so the public event page
 * would otherwise still show whatever the organizer typed by hand. It is opt-in
 * and stated, not silent.
 */

const ERRORS: Record<string, string> = {
  venue_already_confirmed:
    'This event already has a confirmed venue. Cancel that booking before requesting another.',
  event_not_found: 'That event no longer exists.',
  not_your_event: 'You can only request venues for your own events.',
  venue_not_found: 'That venue no longer exists.',
};

export function VenueBrowse() {
  const [events, setEvents] = useState<OrganizerEvent[] | null>(null);
  const [eventId, setEventId] = useState('');
  const [busyVenue, setBusyVenue] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [alsoSetDisplay, setAlsoSetDisplay] = useState(true);

  const load = useCallback(() => {
    listMyEvents()
      .then((rows) => {
        setEvents(rows);
        if (rows.length > 0) setEventId((cur) => cur || rows[0].id);
      })
      .catch((err: unknown) =>
        setError(err instanceof ApiError ? err.message : 'Could not load your events.'));
  }, []);

  useEffect(load, [load]);

  const selected = events?.find((e) => e.id === eventId) ?? null;

  async function request(venue: DirectoryVenue) {
    if (!selected) return;
    setBusyVenue(venue.venueId);
    setError(null);
    setFlash(null);
    try {
      const r = await requestVenue(venue.venueId, selected.id);
      if (alsoSetDisplay) {
        // The engine keeps venue_profile_id and the free-text venue fields separate,
        // so the event page would otherwise not mention the room being requested.
        await updateEvent(selected.id, {
          venueName: venue.venueName,
          venueCity: venue.city ?? undefined,
          venueState: venue.state ?? undefined,
        });
      }
      setFlash(
        `Requested ${venue.venueName} for ${selected.title}. ${VENUE_BOOKING_LABEL[r.status]}.`
        + (alsoSetDisplay ? ' The event’s displayed venue was updated to match.' : ''),
      );
      load();
    } catch (err) {
      // The engine answers { error: '<code>' }, which apiClient surfaces as the message.
      const code = err instanceof ApiError ? err.message : '';
      setError(ERRORS[code] ?? (err instanceof ApiError ? err.message : 'Could not send the request.'));
    } finally {
      setBusyVenue(null);
    }
  }

  return (
    <div className="org-page">
      <header className="org-header">
        <div>
          <h1 className="org-title">Venues</h1>
          <p className="org-lede">
            Browse rooms and send a booking request. The venue confirms or declines it.
          </p>
        </div>
      </header>

      {error && <p className="org-error" role="alert">{error}</p>}
      {flash && <p className="org-flash" role="status">{flash}</p>}

      {events !== null && events.length === 0 ? (
        <p className="org-hint">
          A venue request is always for a specific event, and you do not have one yet.
          Create an event first, then come back to choose a room.
        </p>
      ) : (
        <>
          <section className="org-card">
            <h2 className="org-section__title">Requesting for</h2>
            <div className="adm-filters">
              <select className="adm-select" value={eventId}
                onChange={(e) => setEventId(e.target.value)}>
                {events?.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.title}, {e.date ? formatDate(e.date) : 'no date'}
                  </option>
                ))}
              </select>
            </div>
            <label className="org-check">
              <input type="checkbox" checked={alsoSetDisplay}
                onChange={(e) => setAlsoSetDisplay(e.target.checked)} />
              <span>
                Also set this event&rsquo;s displayed venue name and city to match.
                Voleto keeps the booked venue and the text shown on your event page as
                separate fields, so without this the page keeps whatever you typed.
              </span>
            </label>
          </section>

          <VenuePicker
            eventDate={selected?.date ?? null}
            expectedSize={selected?.capacity ?? null}
            sizeLabel="ticket capacity"
            onSelect={request}
            busyVenueId={busyVenue}
          />
        </>
      )}
    </div>
  );
}
